import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';

import Auth from 'modules/Auth';
import { MAX_SAFE_INTEGER_ALLOWED_IN_BACKEND } from 'modules/api/constants';
import { DealType, DealStatus } from 'components/common/types/Deal.types';
import { formatValue, FormatTypeEnum } from 'modules/I18N';
import { PermissionsEnum } from 'consts/permissions';
import warningSvg from 'assets/icons/warning.svg';
import { RootState } from 'store';

import InputNumber from 'lib/InputNumber';
import InfoMessage from 'components/common/Deal/InfoMessage';
import { INFO_TYPE } from 'components/common/Deal/InfoMessage/InfoMessage.types';
import { Store } from 'components/common/types/Store.types';
import { changeDealCurrentLineData, changeFormParams } from 'store/dealManagement/reducer';
import { getAllocatedImpressions } from 'store/dealManagement/selectors/availabilityAssets';
import { getCurrentLineStartDate } from 'store/dealManagement/selectors/getCurrentLineSchedule';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { isFutureDate } from 'utils/dateUtil';
import RequiredAsterisk from 'components/common/RequiredAsterisk';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';

type ImpressionsProps = {
  isDisabled: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const Impressions = ({ isDisabled }: ImpressionsProps): React.ReactElement | null => {
  const dispatch = useDispatch();
  const isActionsDisabled = useSelector((state: Store) => state.dealManagement.isActionsDisabled);
  const dealType = useSelector(getDealType);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const impressions = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.impressions);
  const budget = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.budget);
  const deliveredImpressions = useSelector(
    (state: Store) => state.dealManagement.commonDeal.currentLine.deliveredImpressions,
  );
  const allocatedImpressions = useSelector(getAllocatedImpressions);
  const startDate = useSelector(getCurrentLineStartDate);

  const { currencyCode, localeCode } = useSelector((state: RootState) => state.publisher.configuration);
  const hasNonGuaranteedExtendedTargetEnabled = useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET);

  const isCurrentLineFutureStartDate = startDate && isFutureDate(startDate);
  const isLive = bookingStatusCode === DealStatus.LIVE;
  const isDealTypeGuaranteed = dealType === DealType.GUARANTEED;

  const [impressionsInput, setImpressionsInput] = useState<number>(impressions || 0);
  const [showImpressionsMessage, setShowImpressionsMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    setShowImpressionsMessage(!!allocatedImpressions && !!impressions && allocatedImpressions !== impressions);
    setImpressionsInput(impressions || 0);
  }, [impressions, allocatedImpressions]);

  const changeImpressions = (value: number): void => {
    const parsedValue = Number(value);

    if (Number.isInteger(parsedValue) && parsedValue <= MAX_SAFE_INTEGER_ALLOWED_IN_BACKEND) {
      setImpressionsInput(parsedValue);
      dispatch(changeDealCurrentLineData({ impressions: parsedValue }));
    }
  };

  const onChange = (value: number): void => {
    setImpressionsInput(Number(value));

    if (!isActionsDisabled) {
      dispatch(changeFormParams({ isActionsDisabled: true }));
    }
  };

  const checkImpressionsChange = async (): Promise<string | void> =>
    new Promise((resolve, reject) => {
      if ([DealStatus.APPROVED, DealStatus.PENDING_APPROVAL, DealStatus.REJECTED].includes(bookingStatusCode)) {
        return resolve();
      }
      if (
        isLive &&
        (isDealTypeGuaranteed || hasNonGuaranteedExtendedTargetEnabled) &&
        impressionsInput === Math.ceil(deliveredImpressions) &&
        !isCurrentLineFutureStartDate
      ) {
        return reject(
          new Error(
            'The impression goal you entered has already been met. Please increase your impression target above the traded impressions in order to save your changes.',
          ),
        );
      }
      if (
        isLive &&
        (isDealTypeGuaranteed || hasNonGuaranteedExtendedTargetEnabled) &&
        impressionsInput <= Math.ceil(deliveredImpressions) &&
        !isCurrentLineFutureStartDate
      ) {
        return reject(new Error('Cannot decrease the impression goal below the number of traded impressions'));
      }
      return resolve();
    });

  const onBlur = async (): Promise<void> => {
    try {
      await checkImpressionsChange();
      changeImpressions(impressionsInput);
      dispatch(changeFormParams({ isActionsDisabled: false }));
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(String(error));
    }
  };

  const getFieldInfoMessage = (): string | null =>
    errorMessage ||
    (showImpressionsMessage
      ? `Your impression target of ${formatValue(impressionsInput, FormatTypeEnum.NUMBER, currencyCode, localeCode)} is ${
          impressionsInput < allocatedImpressions ? 'less' : 'more'
        } than the allocated number of impressions ${formatValue(allocatedImpressions, FormatTypeEnum.NUMBER, currencyCode, localeCode)}. Input your delivery targets for this deal line`
      : null);

  if (!Auth.hasPermission(PermissionsEnum.DEAL_IMPRESSIONS_VIEW)) return null;

  const fieldInfoMessage = getFieldInfoMessage();

  return (
    <div>
      <label
        htmlFor="impressions"
        className="sub-header-base text-neutral-950-opacity-60 mb-2 inline-block"
        data-test-id="impressions-header"
      >
        Impressions
        {isDealTypeGuaranteed ? <RequiredAsterisk /> : null}
      </label>
      <div className="relative">
        <InputNumber
          dataTestId="impressions-input"
          name="impressions"
          value={impressionsInput}
          onChange={onChange}
          onBlur={onBlur}
          maximumFractionDigits={0}
          isDisabled={isDisabled || !!budget}
        />
        {fieldInfoMessage ? (
          <div className="absolute top-2 -right-8">
            <InfoMessage
              type={INFO_TYPE.TOOLTIP}
              icon={
                <SVG
                  src={warningSvg}
                  className={errorMessage ? 'fill-current text-pinkRed-700' : 'fill-current text-yellow'}
                />
              }
            >
              <div className="w-48">{fieldInfoMessage}</div>
            </InfoMessage>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Impressions;
